import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  pipes,
  type CardPriority,
  type DocumentOwnerType,
  type PipeCardDetail,
  type PipeOwner,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCpfCnpj,
  formatDateTime,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DetailTabs } from '@/shared/ui/DetailLayout';
import { Drawer } from '@/shared/ui/Drawer';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { OperationAuthorityPanel } from '../creditAuthority/OperationAuthorityPanel';
import { DocumentsPanel } from '../documents/DocumentsPanel';
import { CommentsPanel } from '../operations/DemandPanels';
import { BrokerSelfSignupCardPanel } from './BrokerSelfSignupCardPanel';
import { podeMoverNaEsteira, tituloDaGaveta, tituloDoCartao } from './cartao';
import {
  BrokerDispatchDocuments,
  BrokerDispatchFollowUp,
  BrokerDispatchList,
  CardSubjectFollowUp,
  CardSubjectGroups,
  CardSubjectPeople,
  CardSubjectPolicyholder,
  CardSubjectRequest,
  ConditionalDraftToggle,
  EngineLaneHistory,
} from './PipeCardSubjectPanels';
import { PipeMoveDialog, type PipeMoveTarget } from './PipeMoveDialog';

type Aba =
  | 'resumo'
  | 'solicitacao'
  | 'pedido'
  | 'tomador'
  | 'autocadastro'
  | 'motor'
  | 'follow-up'
  | 'documentos'
  | 'historico';

const abaFollowUp = { key: 'follow-up', title: 'Follow up' } as const;
const abaDocumentos = { key: 'documentos', title: 'Documentos solicitados' } as const;
const abaHistorico = { key: 'historico', title: 'Historico do cartao' } as const;

/**
 * As abas da gaveta pelo objeto do cartao (P-EST-2). A cotacao tem as abas do OnPoint,
 * nesta ordem, e a esteira PEP nao tem Follow up, como no OnPoint. O cartao de
 * nomeacao tem a aba Solicitacao; o de revisao de limite e o de tomador, o pedido ou o
 * tomador, o Follow up do cartao e os documentos do objeto.
 */
function abasDoCartao(cartao: PipeCardDetail): Array<{ key: Aba; title: string }> {
  switch (cartao.subject_type) {
    case 'nomeacao':
      return [
        { key: 'solicitacao', title: 'Solicitacao' },
        abaDocumentos,
        abaHistorico,
      ];
    case 'revisao_limite':
      return [
        { key: 'pedido', title: 'Revisao de Limite' },
        abaFollowUp,
        abaDocumentos,
        abaHistorico,
      ];
    case 'tomador':
      return [
        { key: 'tomador', title: 'Dados do tomador' },
        abaFollowUp,
        abaDocumentos,
        abaHistorico,
      ];
    // Autocadastro da corretora (P-ACE-2): o pedido, decidido no proprio painel.
    case 'autocadastro_corretora':
      return [
        { key: 'autocadastro', title: 'Autocadastro' },
        abaFollowUp,
        abaHistorico,
      ];
  }
  return [
    { key: 'resumo', title: 'Resumo da cotacao' },
    { key: 'motor', title: 'Historico do motor' },
    ...(cartao.pipe.key === 'pep' ? [] : [abaFollowUp]),
    abaDocumentos,
    abaHistorico,
  ];
}

/** O dono dos documentos solicitados do cartao: o proprio objeto do cartao. */
const donoDosDocumentos: Record<string, DocumentOwnerType> = {
  operacao: 'operacao',
  tomador: 'tomador',
  nomeacao: 'nomeacao',
  revisao_limite: 'revisao_limite',
};

/**
 * Gaveta do cartao da esteira ("Dados da Cotacao" e "Dados da Solicitacao de
 * Nomeacao" do OnPoint).
 *
 * No topo, Responsavel e Prioridade, que gravam ao mudar; depois os movimentos que o
 * servidor informou, com o que cada um exige; e as abas do objeto do cartao. O detalhe
 * completo da cotacao continua na operacao: o link leva a ela.
 */
export function PipeCardDrawer({
  cardId,
  pipeId,
  owners,
  priorities,
  onClose,
}: {
  cardId: string | null;
  pipeId: string;
  owners: PipeOwner[];
  priorities: CardPriority[];
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aba, setAba] = useState<Aba>('resumo');
  const [movendo, setMovendo] = useState<PipeMoveTarget | null>(null);

  const consulta = useQuery({
    queryKey: ['pipe-cards', cardId],
    queryFn: ({ signal }) => pipes.card(cardId ?? '', signal),
    enabled: cardId !== null,
  });

  const atribuir = useMutation({
    mutationFn: (input: { owner_member_id?: string | null; priority_id?: string }) =>
      pipes.updateCard(cardId ?? '', input),
    onSuccess: (cartao) => {
      toast.success('Cartao atualizado.');
      queryClient.setQueryData(['pipe-cards', cardId], cartao);
      void queryClient.invalidateQueries({ queryKey: ['pipes'] });
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const cartao = consulta.data;
  const podeAtribuir = can('esteiras.cartao.update');
  const podeMover = podeMoverNaEsteira(can, cartao?.pipe);
  const abas = cartao ? abasDoCartao(cartao) : [];
  const abaAtiva: Aba = abas.some((a) => a.key === aba)
    ? aba
    : (abas[0]?.key ?? 'resumo');
  const envios = cartao?.dispatches ?? [];

  return (
    <Drawer
      open={cardId !== null}
      onClose={onClose}
      title={cartao ? tituloDaGaveta(cartao) : 'Cartao da esteira'}
      description={
        cartao
          ? `${tituloDoCartao(cartao)} - ${cartao.pipe.name} > ${cartao.step.name}`
          : undefined
      }
      size="xl"
    >
      {consulta.isPending ? (
        <SkeletonText lines={8} />
      ) : consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel abrir o cartao">
          {consulta.error.message}
        </Alert>
      ) : cartao ? (
        <div className="flex flex-col gap-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Responsavel"
              placeholder="Sem responsavel"
              disabled={!podeAtribuir || atribuir.isPending}
              options={opcoesDeResponsavel(owners, cartao)}
              value={cartao.owner_member_id ?? ''}
              onChange={(event) =>
                atribuir.mutate({ owner_member_id: event.target.value || '' })
              }
            />
            <Select
              label="Prioridade"
              disabled={!podeAtribuir || atribuir.isPending}
              options={priorities
                .filter((p) => p.active || p.id === cartao.priority?.id)
                .map((p) => ({ value: p.id, label: p.name }))}
              value={cartao.priority?.id ?? ''}
              onChange={(event) => {
                if (event.target.value)
                  atribuir.mutate({ priority_id: event.target.value });
              }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge kind="colunaEsteira" value={cartao.step.type} />
            {cartao.is_closed ? <Badge tone="neutral">Encerrado</Badge> : null}
            {cartao.is_overdue ? <Badge tone="danger">Prazo vencido</Badge> : null}
            {cartao.due_at && !cartao.is_closed ? (
              <span className="text-2xs text-ink-500">
                Prazo da coluna: {formatDateTime(cartao.due_at)}
              </span>
            ) : null}
            <span className="text-2xs text-ink-500">
              Na coluna desde {formatDateTime(cartao.entered_step_at)}
            </span>
          </div>

          {!cartao.is_closed && cartao.targets.length > 0 ? (
            <section aria-label="Mover o cartao" className="flex flex-col gap-2">
              <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
                Mover para
              </p>
              <div className="flex flex-wrap gap-2">
                {cartao.targets.map((alvo) => (
                  <Button
                    key={alvo.step.id}
                    size="sm"
                    variant={alvo.step.type === 'aprovado' ? 'primary' : 'secondary'}
                    disabled={!podeMover || !alvo.allowed}
                    title={alvo.blocked_reason || alvo.operation_label || undefined}
                    onClick={() =>
                      setMovendo({
                        stepId: alvo.step.id,
                        stepName: alvo.step.name,
                        operationLabel: alvo.operation_label,
                        requiresReason: alvo.requires_reason,
                        justification: alvo.justification,
                      })
                    }
                  >
                    {alvo.step.name}
                  </Button>
                ))}
              </div>
              {cartao.targets.some((alvo) => !alvo.allowed && alvo.blocked_reason) ? (
                <ul className="flex flex-col gap-0.5 text-2xs text-ink-500">
                  {cartao.targets
                    .filter((alvo) => !alvo.allowed && alvo.blocked_reason)
                    .map((alvo) => (
                      <li key={alvo.step.id}>
                        {alvo.step.name}: {alvo.blocked_reason}
                      </li>
                    ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          <DetailTabs
            label="Abas do cartao"
            tabs={abas}
            active={abaAtiva}
            onChange={setAba}
          />

          {abaAtiva === 'resumo' ? (
            <div className="flex flex-col gap-4">
              <ResumoDaCotacao
                cartao={cartao}
                podeEditar={can('operacoes.operacao.update')}
              />
              <CardSubjectGroups cartao={cartao} />
              <CardSubjectPeople cartao={cartao} />
            </div>
          ) : null}

          {abaAtiva === 'solicitacao' || abaAtiva === 'pedido' ? (
            <CardSubjectRequest cartao={cartao} />
          ) : null}

          {abaAtiva === 'tomador' ? <CardSubjectPolicyholder cartao={cartao} /> : null}

          {abaAtiva === 'autocadastro' ? (
            <BrokerSelfSignupCardPanel registrationId={cartao.subject_id} />
          ) : null}

          {abaAtiva === 'motor' && cartao.operation_id ? (
            <div className="flex flex-col gap-5">
              <EngineLaneHistory operationId={cartao.operation_id} />
              {/* Impedimentos, alcada e resultado do motor (C-ALC). */}
              <OperationAuthorityPanel operationId={cartao.operation_id} compact />
            </div>
          ) : null}

          {abaAtiva === 'follow-up' && cartao.operation_id ? (
            <div className="flex flex-col gap-3">
              <CommentsPanel
                operationId={cartao.operation_id}
                originPipeId={cartao.pipe.id}
                title="Follow up"
                description="Registros da cotacao, com a esteira de origem de cada um."
              />
              <BrokerDispatchFollowUp cartao={cartao} />
              {cartao.pipe.key === 'subscricao' ? (
                <ConditionalDraftToggle operationId={cartao.operation_id} />
              ) : null}
              <BrokerDispatchList
                envios={envios.filter((e) => e.kind === 'follow_up')}
              />
            </div>
          ) : null}

          {abaAtiva === 'follow-up' && !cartao.operation_id ? (
            <div className="flex flex-col gap-3">
              <CardSubjectFollowUp cartao={cartao} />
              <BrokerDispatchList
                envios={envios.filter((e) => e.kind === 'follow_up')}
              />
            </div>
          ) : null}

          {abaAtiva === 'documentos' && donoDosDocumentos[cartao.subject_type] ? (
            <div className="flex flex-col gap-3">
              <DocumentsPanel
                ownerType={donoDosDocumentos[cartao.subject_type] ?? 'operacao'}
                ownerId={cartao.subject_id}
                title="Documentos solicitados"
                description={
                  cartao.operation_id
                    ? 'Documentos pedidos na cotacao. O pedido feito aqui guarda o cartao de origem.'
                    : 'Documentos pedidos no objeto deste cartao. O pedido feito aqui guarda o cartao de origem.'
                }
                pipeCardId={cartao.id}
              />
              <BrokerDispatchDocuments cartao={cartao} />
              <BrokerDispatchList
                envios={envios.filter((e) => e.kind === 'documentos')}
              />
            </div>
          ) : null}

          {abaAtiva === 'historico' ? <HistoricoDoCartao cardId={cartao.id} /> : null}

          <PipeMoveDialog
            cardId={cartao.id}
            pipeId={pipeId}
            target={movendo}
            onClose={() => setMovendo(null)}
            onMoved={(atualizado) =>
              queryClient.setQueryData(['pipe-cards', cardId], atualizado)
            }
          />
        </div>
      ) : null}
    </Drawer>
  );

  function opcoesDeResponsavel(lista: PipeOwner[], card: PipeCardDetail) {
    const opcoes = lista.map((o) => ({ value: o.member_id, label: o.name }));
    // O responsavel atual que deixou de ser elegivel continua aparecendo.
    if (
      card.owner_member_id &&
      !lista.some((o) => o.member_id === card.owner_member_id)
    ) {
      opcoes.unshift({
        value: card.owner_member_id,
        label: card.owner_label || 'Responsavel atual',
      });
    }
    return opcoes;
  }
}

function ResumoDaCotacao({
  cartao,
  podeEditar,
}: {
  cartao: PipeCardDetail;
  podeEditar: boolean;
}) {
  const { can } = useAuth();
  const o = cartao.operation;
  if (!o) {
    return (
      <EmptyState
        icon="file"
        title="Cartao sem cotacao"
        description="A cotacao deste cartao nao foi encontrada ou nao esta no seu acesso."
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Dado rotulo="No da cotacao" valor={o.proposal_number || String(o.number)} />
        <Dado rotulo="Data de cadastro" valor={formatDateTime(o.created_at)} />
        <div className="min-w-0">
          <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
            Status
          </p>
          <div className="mt-1">
            {o.proposal_status ? (
              <StatusBadge kind="cotacao" value={o.proposal_status} />
            ) : (
              <StatusBadge kind="operacao" value={o.stage} />
            )}
          </div>
        </div>
        <Dado rotulo="Corretora" valor={o.broker?.label ?? 'Operacao direta'} />
        <Dado
          rotulo="CNPJ da corretora"
          valor={o.broker?.document ? formatCpfCnpj(o.broker.document) : '-'}
        />
        <Dado rotulo="Tomador" valor={o.policyholder.label} />
        <Dado
          rotulo="Cnpj"
          valor={o.policyholder.document ? formatCpfCnpj(o.policyholder.document) : '-'}
          extra={
            can('tomadores.tomador.view') ? (
              <Link
                to={`/tomadores/${o.policyholder.id}`}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Visualizar cadastro completo do tomador
              </Link>
            ) : null
          }
        />
        <Dado rotulo="Segurado" valor={o.insured?.label ?? 'Sem segurado'} />
        <Dado
          rotulo="CPF/CNPJ"
          valor={o.insured?.document ? formatCpfCnpj(o.insured.document) : '-'}
        />
        <Dado rotulo="Grupo Principal" valor={o.modality_group || '-'} />
        <Dado rotulo="Modalidade Principal" valor={o.modality} />
        <Dado rotulo="Importancia Segurada" valor={formatMoney(o.insured_amount)} />
        <Dado rotulo="Inicio Vigencia" valor={formatCivilDate(o.coverage_start)} />
        <Dado rotulo="Fim Vigencia" valor={formatCivilDate(o.coverage_end)} />
        <Dado rotulo="Prazo" valor={o.term_days ? `${o.term_days} dias` : '-'} />
        <Dado rotulo="Premio" valor={formatMoney(o.premium)} />
        <Dado rotulo="Taxa" valor={formatRate(o.applied_rate_bps)} />
        <Dado
          rotulo="Valor da Comissao"
          valor="Regra pendente"
          extra={
            <span className="text-2xs text-ink-500">
              O valor nasce na emissao; a conta na cotacao depende de regra (COR-005).
            </span>
          }
        />
        <Dado rotulo="% Comissao" valor={formatRate(o.commission_bps)} />
      </div>

      {cartao.rules.length > 0 ? (
        <div className="rounded-md border border-line p-3">
          <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
            Regra Reprovada
          </p>
          <ul className="mt-1 flex flex-col gap-1 text-sm text-ink-700">
            {cartao.rules.map((regra) => (
              <li key={regra.id}>
                <span className="font-medium">{regra.rule_id}</span>
                {regra.message ? ` - ${regra.message}` : ''}
                {regra.lane ? (
                  <span className="text-2xs text-ink-500">
                    {` (motor, raia ${regra.lane})`}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Link
          to={`/operacoes/${o.id}`}
          className={buttonClassName({ variant: 'secondary', size: 'md' })}
        >
          {podeEditar
            ? 'Editar informacoes da cotacao'
            : 'Visualizar informacoes da cotacao'}
        </Link>
      </div>
    </div>
  );
}

function HistoricoDoCartao({ cardId }: { cardId: string }) {
  const consulta = useQuery({
    queryKey: ['pipe-cards', cardId, 'events'],
    queryFn: ({ signal }) => pipes.events(cardId, signal),
  });
  if (consulta.isPending) return <SkeletonText lines={4} />;
  if (consulta.isError) {
    return (
      <Alert tone="danger" title="Nao foi possivel carregar o historico do cartao">
        {consulta.error.message}
      </Alert>
    );
  }
  const eventos = [...consulta.data].reverse();
  if (eventos.length === 0) {
    return (
      <EmptyState
        icon="clock"
        title="Sem historico"
        description="Nada aconteceu com o cartao ainda."
      />
    );
  }
  return (
    <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
      {eventos.map((evento) => (
        <li key={evento.id} className="flex flex-col gap-1 px-3 py-2.5">
          <p className="flex flex-wrap items-center gap-1.5 text-xs">
            <StatusBadge kind="eventoCartao" value={evento.kind} />
            <StatusBadge kind="origemCartao" value={evento.origin} />
            {evento.from_step || evento.to_step ? (
              <span className="text-ink-700">
                {evento.from_step ? `${evento.from_step.name} > ` : ''}
                {evento.to_step?.name ?? ''}
              </span>
            ) : null}
          </p>
          {evento.decline_reason_label ? (
            <p className="text-xs text-ink-700">
              Motivo: {evento.decline_reason_label}
            </p>
          ) : null}
          {evento.justification_text ? (
            <p className="text-xs text-ink-700">
              Justificativa: {evento.justification_text}
            </p>
          ) : null}
          <p className="text-2xs text-ink-500">
            {evento.actor_label || 'Sistema'} - {formatDateTime(evento.created_at)}
          </p>
        </li>
      ))}
    </ul>
  );
}

function Dado({
  rotulo,
  valor,
  extra,
}: {
  rotulo: string;
  valor: string;
  extra?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <p className="mt-0.5 truncate text-sm font-medium text-ink-900">{valor}</p>
      {extra ? <div className="mt-1">{extra}</div> : null}
    </div>
  );
}
